import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import theme from './theme';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import Home from './pages/Home';
import Diensten from './pages/Diensten';
import Over from './pages/Over';
import Contact from './pages/Contact';
import Tips from './pages/Tips';
import TipArticle from './pages/TipArticle';
import Events from './pages/Events';
import Login from './pages/Login';
import PremiumActivation from './pages/PremiumActivation';
import AfspraakPage from './pages/Afspraak';
import CoachDashboard from './pages/CoachDashboard';
import IndividueleCoaching from './pages/diensten/IndividueleCoaching';
import Workshops from './pages/diensten/Workshops';
import Lesgeven from './pages/diensten/Lesgeven';
import Zelfcoaching from './pages/Zelfcoaching';
import Privacy from './pages/Privacy';
import Voorwaarden from './pages/Voorwaarden';
import ForgotPassword from './pages/ForgotPassword';
import MyContent from './pages/MyContent';
import WhyMePage from './pages/WhyMePage';
import PremiumRedirect from './components/PremiumRedirect';
import Loopbaancoaching from './pages/Loopbaancoaching';
import RouteMetadata from './components/RouteMetadata';

import './App.css';

// ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// ProtectedRoute component
const ProtectedRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>; // Or a loading spinner
  }

  if (!currentUser) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// CoachRoute component
const CoachRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if the user has the 'coach' role (adjust based on your user object structure)
  if (currentUser.role !== 'coach') {
    // Redirect non-coach users to a different page, e.g., home or their dashboard
    return <Navigate to="/" replace />;
  }

  return children;
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <LanguageProvider>
          <Router>
            <ScrollToTop />
            <RouteMetadata />
            <Navigation />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/diensten" element={<Diensten />} />
              <Route path="/loopbaancoaching" element={<Loopbaancoaching />} />
              <Route path="/diensten/individuele-coaching" element={<IndividueleCoaching />} />
              <Route path="/sollicitatietraining" element={<Workshops />} />
              <Route path="/diensten/workshops" element={<Workshops />} />
              <Route path="/lesgeven-public-speaking" element={<Lesgeven />} />
              <Route path="/diensten/lesgeven" element={<Lesgeven />} />
              <Route path="/zelfcoaching" element={<Zelfcoaching />} />
              <Route path="/diensten/zelfcoaching" element={<Zelfcoaching />} />
              <Route path="/over" element={<Over />} />
              <Route path="/waarom-ik" element={<WhyMePage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/tips" element={<Tips />} />
              <Route path="/tips/:slug" element={<TipArticle />} />
              <Route path="/events" element={<Events />} />
              <Route path="/premium" element={<PremiumRedirect />} />
              <Route path="/premium-activation" element={<PremiumActivation />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/afspraak" element={<AfspraakPage />} />
              <Route path="/coach" element={<CoachDashboard />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/voorwaarden" element={<Voorwaarden />} />
              <Route path="/mijn-content" element={<MyContent />} />
            </Routes>
            <Footer />
          </Router>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
